import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useUserData } from '../lib/useUserData.js'
import { useToast } from '../context/ToastContext.jsx'
import { calculate, DOSE_UNITS, SYRINGES } from '../lib/peptide.js'
import ScheduleModal from '../components/ScheduleModal.jsx'
import EditCalcModal from '../components/EditCalcModal.jsx'
import Seo from '../components/Seo.jsx'

const unitLabel = (id) => DOSE_UNITS.find((u) => u.id === id)?.label || 'mcg'
const capacityOf = (id) => SYRINGES.find((s) => s.id === id)?.capacity ?? 100
const fmtLogTime = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
  } catch { return '' }
}

export default function Saved() {
  const { calcs, loading, deleteCalc, updateCalc, doseLogs, logDose, deleteDoseLog } = useUserData()
  const toast = useToast()
  const navigate = useNavigate()
  const [scheduling, setScheduling] = useState(null)
  const [editing, setEditing] = useState(null)

  const openInCalculator = (c) => {
    navigate('/calculator', {
      state: {
        calc: {
          vialMg: c.vialMg, waterMl: c.waterMl, dose: c.dose, doseUnit: c.doseUnit,
          syringe: c.syringe, name: c.name, extras: c.snapshot?.extras || [],
        },
      },
    })
  }

  const doDelete = async (c) => {
    if (window.confirm(`Delete "${c.name}"?`)) {
      const res = await deleteCalc(c.id)
      toast(res?.error || 'Calculation deleted', res?.error ? 'red' : 'default')
    }
  }

  const saveEdit = async (fields) => {
    const res = await updateCalc(editing.id, fields)
    if (res?.error) return toast(res.error, 'red')
    toast('Calculation updated', 'green')
    setEditing(null)
  }

  // vial tracking helpers (dosesUsed lives in the calc snapshot)
  const setUsed = async (c, used, total) => {
    const clamped = Math.max(0, Math.min(used, total))
    const res = await updateCalc(c.id, { snapshot: { ...(c.snapshot || {}), dosesUsed: clamped } })
    if (res?.error) toast(res.error, 'red')
  }

  // Log a dose: bump the vial counter AND write a timestamped history entry.
  const doLogDose = (c, used, total, units) => {
    setUsed(c, used + 1, total)
    logDose({ calcId: c.id, name: c.name, dose: c.dose, doseUnit: c.doseUnit, units })
  }
  // Undo: decrement the counter and remove that calc's most recent log entry.
  const undoDose = (c, used, total) => {
    setUsed(c, used - 1, total)
    const last = doseLogs.find((d) => d.calcId === c.id) // doseLogs is newest-first
    if (last) deleteDoseLog(last.id)
  }

  return (
    <main className="page">
      <Seo title="My Saved Calculations | CalculateMyPeptides" noindex />
      <div className="container">
        <div className="between wrap" style={{ marginBottom: 22 }}>
          <div>
            <h1 className="section-title">My Calculations</h1>
            <p className="section-sub mb-0">
              {calcs.length ? `${calcs.length} saved protocol${calcs.length > 1 ? 's' : ''} — track each vial as you use it.` : 'Your saved peptide protocols live here.'}
            </p>
          </div>
          <Link to="/calculator" className="btn btn-primary btn-sm">+ New calculation</Link>
        </div>

        {loading ? (
          <div className="card empty"><div className="spinner" /><p style={{ marginTop: 12 }}>Loading your calculations…</p></div>
        ) : calcs.length === 0 ? (
          <div className="card empty">
            <div className="emoji">🧪</div>
            <h3 style={{ marginBottom: 6 }}>No saved calculations yet</h3>
            <p style={{ marginBottom: 18 }}>Run a calculation and hit “Save” to keep it here.</p>
            <Link to="/calculator" className="btn btn-primary">Open the calculator</Link>
          </div>
        ) : (
          <div className="calc-list">
            {calcs.map((c) => {
              const res = calculate(c)
              const units = res.ok ? res.units : (c.snapshot?.units ?? '—')
              const total = res.ok ? res.dosesPerVial : 0
              const used = Math.min(c.snapshot?.dosesUsed || 0, total)
              const remaining = Math.max(0, total - used)
              const pct = total > 0 ? Math.round((used / total) * 100) : 0
              const cap = capacityOf(c.syringe)
              return (
                <div className="card calc-item fade-in" key={c.id}>
                  <div className="ci-head">
                    <h3>{c.name}</h3>
                    <button className="icon-btn" style={{ width: 32, height: 32, fontSize: '.9rem' }}
                      onClick={() => setEditing(c)} title="Edit">✏️</button>
                  </div>

                  <div className="calc-big">
                    <span className="u">{units}</span>
                    <span className="l">units · {res.volumeMl} mL</span>
                  </div>

                  {res.ok && (
                    <p className="calc-plain">
                      Draw <strong>{units} units</strong> into a {cap}-unit syringe for each{' '}
                      <strong>{c.dose} {unitLabel(c.doseUnit)}</strong> dose — about {total} doses per vial.
                    </p>
                  )}

                  <div className="calc-meta">
                    <span className="pill">{c.vialMg} mg vial</span>
                    <span className="pill">{c.waterMl} mL water</span>
                    <span className="pill blue">{c.dose} {unitLabel(c.doseUnit)} dose</span>
                  </div>

                  {/* vial tracker */}
                  {res.ok && total > 0 && (
                    <div className="vial-track">
                      <div className="vial-track-head">
                        <span className="vial-track-label">{remaining} of {total} doses left</span>
                        {remaining === 0 && <span className="pill amber">Vial finished</span>}
                      </div>
                      <div className="vial-bar"><div className="vial-bar-fill" style={{ width: `${pct}%` }} /></div>
                      <div className="vial-actions">
                        <button className="btn btn-ghost btn-sm" onClick={() => undoDose(c, used, total)} disabled={used === 0}>− Undo</button>
                        <button className="btn btn-soft btn-sm" onClick={() => doLogDose(c, used, total, res.ok ? res.units : c.snapshot?.units)} disabled={remaining === 0}>＋ Log dose</button>
                        {used > 0 && <button className="btn btn-ghost btn-sm" onClick={() => setUsed(c, 0, total)}>↺ Refill</button>}
                      </div>
                    </div>
                  )}

                  <div className="calc-actions">
                    <button className="btn btn-soft btn-sm" onClick={() => setScheduling(c)}>🗓️ Schedule</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => openInCalculator(c)}>Open</button>
                    <button className="btn btn-danger btn-sm" onClick={() => doDelete(c)}>Delete</button>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {doseLogs.length > 0 && (
          <section style={{ marginTop: 36 }}>
            <h2 className="section-title" style={{ fontSize: '1.35rem' }}>Dose history</h2>
            <p className="section-sub" style={{ marginTop: 0 }}>Every dose you’ve logged, most recent first.</p>
            <div className="card dose-log-card">
              {doseLogs.slice(0, 60).map((d) => (
                <div className="dose-log-row" key={d.id}>
                  <div className="dose-log-main">
                    <span className="dose-log-name">{d.name || 'Dose'}</span>
                    <span className="dose-log-meta">
                      {d.dose != null ? `${d.dose} ${unitLabel(d.doseUnit)}` : ''}
                      {d.units != null ? ` · ${d.units} units` : ''}
                    </span>
                  </div>
                  <span className="dose-log-time">{fmtLogTime(d.loggedAt)}</span>
                  <button className="dose-log-del" onClick={() => deleteDoseLog(d.id)} aria-label="Remove entry" title="Remove">✕</button>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      {editing && (
        <EditCalcModal calc={editing} onClose={() => setEditing(null)} onSave={saveEdit} />
      )}

      {scheduling && (
        <ScheduleModal
          calc={scheduling}
          onClose={() => setScheduling(null)}
          onError={(msg) => { setScheduling(null); toast(msg, 'red') }}
          onScheduled={(n) => {
            setScheduling(null)
            toast(`${n} dose${n > 1 ? 's' : ''} added to calendar`, 'green')
            navigate('/calendar')
          }}
        />
      )}
    </main>
  )
}
